import _ from "lodash";

import { newId } from "../id/id";
import {
  AddonTemplateChecklistDocumentElement,
  isAddonTemplateChecklist,
} from "../model/addonTemplate";
import {
  ChecklistContents,
  ChecklistDocumentElement,
  ChecklistElement,
  ChecklistHeader,
  ChecklistItem,
  EditableString,
  isChecklistHeader,
  isChecklistItem,
  isPageBreak,
  pageBreak,
} from "../model/checklistAppState";
import {
  isSerializedChecklist,
  isSerializedChecklistHeader,
  isSerializedChecklistItem,
  isSerializedPageBreak,
  SerializedChecklistContents,
  SerializedChecklistDocument,
  SerializedChecklistDocumentElement,
  SerializedChecklistElement,
  SerializedChecklistHeader,
  SerializedChecklistItem,
} from "../model/checklistSerialized";
import { ChecklistDocument } from "../model/editorState";

function editableString(contents: string): EditableString {
  return {
    content: contents,
    editing: false,
    id: newId(),
  };
}

export function appStateFromSerialized(incoming: SerializedChecklistContents): ChecklistContents {
  return {
    title: editableString(incoming.title),
    checklists: incoming.checklists.map(deserializeChecklist),
  };
}

function deserializeChecklist(
  checklist: SerializedChecklistDocumentElement
): ChecklistDocumentElement {
  if (isSerializedPageBreak(checklist)) {
    return {
      id: newId(),
      type: pageBreak,
    };
  } else if (isSerializedChecklist(checklist)) {
    return {
      id: newId(),
      type: "checklist",
      title: editableString(checklist.title),
      content: checklist.content.map(deserializeChecklistElement),
      role: checklist.role,
    };
  } else {
    const _exhaustiveCheck: never = checklist;
    return _exhaustiveCheck;
  }
}

export function deserializeChecklistElement(
  checklistElement: SerializedChecklistElement
): ChecklistElement {
  if (isSerializedChecklistItem(checklistElement)) {
    const checklistItem: ChecklistItem = {
      id: newId(),
      type: "item",
      item: editableString(checklistElement.item),
      setting: editableString(checklistElement.setting),
      styles: { ...checklistElement.styles },
    };
    return checklistItem;
  } else if (isSerializedChecklistHeader(checklistElement)) {
    const checklistHeader: ChecklistHeader = {
      id: newId(),
      type: "header",
      text: editableString(checklistElement.header),
      styles: { ...checklistElement.styles },
    };
    return checklistHeader;
  } else {
    const _exhaustiveCheck: never = checklistElement;
    return _exhaustiveCheck;
  }
}

export function serializeChecklistElement(
  checklistElement: ChecklistElement
): SerializedChecklistElement {
  if (isChecklistItem(checklistElement)) {
    const serializedChecklistItem: SerializedChecklistItem = {
      item: checklistElement.item.content,
      setting: checklistElement.setting.content,
      styles: { ...checklistElement.styles },
    };
    return serializedChecklistItem;
  } else if (isChecklistHeader(checklistElement)) {
    const serializedChecklistHeader: SerializedChecklistHeader = {
      header: checklistElement.text.content,
      styles: { ...checklistElement.styles },
    };
    return serializedChecklistHeader;
  } else {
    const _exhaustiveCheck: never = checklistElement;
    return _exhaustiveCheck;
  }
}

export function serializedAppState(incoming: ChecklistContents): SerializedChecklistContents {
  return {
    title: incoming.title.content,
    checklists: incoming.checklists.map((checklist) => {
      if (isPageBreak(checklist)) {
        return pageBreak;
      } else {
        return {
          title: checklist.title.content,
          content: checklist.content.map(serializeChecklistElement),
        };
      }
    }),
  };
}

export function serializedChecklistDocument(
  incoming: ChecklistDocument
): SerializedChecklistDocument {
  return {
    title: incoming.title,
    contents: serializedAppState(incoming.contents),
    styles: _.cloneDeep(incoming.styles),
    layout: incoming.layout,
    options: _.cloneDeep(incoming.options),
  };
}

export function deserializeAircraftTemplate(
  incoming: AddonTemplateChecklistDocumentElement[]
): ChecklistDocumentElement[] {
  return incoming.map((element) => {
    const mapped = isAddonTemplateChecklist(element)
      ? { title: element.title, content: element.content, role: [element.roles[0]] }
      : element;
    return deserializeChecklist(mapped);
  });
}
